"use client";

import { useState } from "react";

import { LocalizedLink as Link } from "@/components/i18n/LocalizedLink";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useConsent } from "@/features/consent/ConsentProvider";
import { ACCEPT_ALL, type ConsentChoice, NECESSARY_ONLY } from "@/features/consent/lib/consent";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * Samtyckesbanner + inställningsdialog. "Acceptera alla" och "Endast
 * nödvändiga" har samma tyngd — IMY godtar inte att nej görs svårare än ja.
 */
export function CookieBanner() {
  const context = useConsent();
  const { t } = useI18n();

  if (!context?.ready) return null;
  const { consent, settingsOpen, saveConsent, openSettings } = context;

  return (
    <>
      {consent === null && !settingsOpen ? (
        <section
          aria-label={t("cookieConsent.bannerAria")}
          // Capped and scrollable so enlarged text can never push the heading
          // or the buttons off-screen.
          className="fixed inset-x-4 bottom-4 z-[70] mx-auto max-h-[calc(100svh-2rem)] max-w-3xl overflow-y-auto rounded-2xl border border-border bg-background p-4 shadow-2xl sm:p-6"
        >
          <h2 className="text-base font-semibold text-foreground">{t("cookieConsent.title")}</h2>
          <p className="mt-2 max-w-prose text-sm leading-6 text-muted-foreground">
            {t("cookieConsent.body")}{" "}
            <Link href="/cookie-policy" className="font-medium text-foreground underline underline-offset-4">
              {t("cookieConsent.policyLink")}
            </Link>
          </p>
          {/* Phones: the two equal-weight choices share a row; customise sits below. */}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:justify-end">
            <Button variant="text" size="sm" onClick={openSettings} className="order-last col-span-2 sm:order-none">
              {t("cookieConsent.customize")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => saveConsent(NECESSARY_ONLY)}>
              {t("cookieConsent.necessaryOnly")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => saveConsent(ACCEPT_ALL)}>
              {t("cookieConsent.acceptAll")}
            </Button>
          </div>
        </section>
      ) : null}
      <CookieSettingsDialog />
    </>
  );
}

function CookieSettingsDialog() {
  const context = useConsent();
  const { t } = useI18n();
  if (!context) return null;

  return (
    <Dialog
      open={context.settingsOpen}
      onOpenChange={(open) => (open ? context.openSettings() : context.closeSettings())}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("cookieConsent.settingsTitle")}</DialogTitle>
          <DialogDescription>{t("cookieConsent.settingsDescription")}</DialogDescription>
        </DialogHeader>
        {/* DialogContent avmonteras när dialogen stängs, så formuläret läser
            in det sparade valet på nytt varje gång den öppnas. */}
        <CookieSettingsForm initial={context.consent} onSave={context.saveConsent} />
      </DialogContent>
    </Dialog>
  );
}

function CookieSettingsForm({
  initial,
  onSave,
}: {
  initial: ConsentChoice | null;
  onSave: (choice: ConsentChoice) => void;
}) {
  const { t } = useI18n();
  const [analytics, setAnalytics] = useState(initial?.analytics ?? false);
  const [marketing, setMarketing] = useState(initial?.marketing ?? false);

  const categories = [
    {
      id: "necessary",
      label: t("cookieConsent.categories.necessary.label"),
      description: t("cookieConsent.categories.necessary.description"),
      checked: true,
      disabled: true,
      onChange: () => {},
    },
    {
      id: "analytics",
      label: t("cookieConsent.categories.analytics.label"),
      description: t("cookieConsent.categories.analytics.description"),
      checked: analytics,
      disabled: false,
      onChange: setAnalytics,
    },
    {
      id: "marketing",
      label: t("cookieConsent.categories.marketing.label"),
      description: t("cookieConsent.categories.marketing.description"),
      checked: marketing,
      disabled: false,
      onChange: setMarketing,
    },
  ];

  return (
    <>
      <ul className="flex flex-col gap-4">
        {categories.map((category) => (
          <li key={category.id} className="flex gap-3">
            <Checkbox
              id={`cookie-${category.id}`}
              checked={category.checked}
              disabled={category.disabled}
              onCheckedChange={(value) => category.onChange(value === true)}
              className="mt-0.5"
            />
            <label htmlFor={`cookie-${category.id}`} className="text-sm">
              <span className="font-medium text-foreground">{category.label}</span>
              <span className="mt-0.5 block leading-6 text-muted-foreground">{category.description}</span>
            </label>
          </li>
        ))}
      </ul>
      <DialogFooter className="gap-2">
        <Button variant="outline" size="sm" onClick={() => onSave({ analytics, marketing })}>
          {t("cookieConsent.save")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => onSave(ACCEPT_ALL)}>
          {t("cookieConsent.acceptAll")}
        </Button>
      </DialogFooter>
    </>
  );
}

/** Länkknapp i sidfoten som öppnar inställningarna igen. */
export function CookieSettingsButton({ className }: { className?: string }) {
  const context = useConsent();
  const { t } = useI18n();
  if (!context) return null;
  return (
    <button type="button" onClick={context.openSettings} className={className}>
      {t("cookieConsent.footerLink")}
    </button>
  );
}
